import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import TeamIosDarkToggle from 'team/shared/components/button/TeamIosDarkToggle';
import { ColorizeOutlinedIcon, LockOutlinedIcon } from 'icons/materialIcons';
import { ChromePicker } from 'react-color';
import { useCardContext } from 'contexts/CardContext'; // Import the CardContext
import { set } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { validateUUID } from 'utils/validator';

const colorOptions = ['#000000', '#eb5757', '#f2994b', '#f2c84b', '#219654', '#2f82ed', '#9b51e0'];

const TeamChooseThemeField = ({ 
    onThemeChange, 
    onLinkColorChange,
    isCardColorRestricted = false,
    canRequestChange = false,
    onRestrictionNotification
}) => {
    const [showCardThemePicker, setShowCardThemePicker] = useState(false);
    const [showLinkColorPicker, setShowLinkColorPicker] = useState(false);
    const [localCardThemeColor, setLocalCardThemeColor] = useState('#b3b3b3'); // Default grey
    const [localLinkColor, setLocalLinkColor] = useState('#b3b3b3'); // Default grey
    const [matchLink, setMatchLink] = useState(false);
    const { cardState, setCardTheme, setCardLinkColor } = useCardContext();
    const { cardId } = useParams(); // Extracts the `cardId` from the URL

    const cardPickerRef = useRef(null);
    const linkPickerRef = useRef(null);




    const {
        cardTheme,
        cardLinkColor,
    } = cardState[cardId] || {};





    const handleCardThemePickerToggle = () => {
        setShowCardThemePicker(!showCardThemePicker);
    };

    const handleLinkColorPickerToggle = () => {
        setShowLinkColorPicker(!showLinkColorPicker);
    };

    const handleCardThemeChangeComplete = (color) => {
        // Check if field is restricted
        if (isCardColorRestricted) {
            const message = canRequestChange
                ? 'Card Color is restricted by your subteam. You can submit a change request.'
                : 'Card Color is restricted by your subteam and cannot be updated.';
            if (onRestrictionNotification) {
                onRestrictionNotification(message);
            }
            return;
        }

        console.log('🔍 ChooseThemeField: handleCardThemeChangeComplete called with:', color);
        console.log('🔍 ChooseThemeField: Before update - localCardThemeColor:', localCardThemeColor, 'cardTheme:', cardTheme);

        setLocalCardThemeColor(color.hex);
        onLinkColorChange(color.hex); // Update link color as well

        onThemeChange(color.hex); // Pass the new theme color to the parent

        setCardTheme(cardId, color.hex);
        if (matchLink) {
            setLocalLinkColor(color.hex); // Match link color to card theme if toggled
            setCardLinkColor(cardId, color.hex);
            console.log("handleLinkColorChangeComplete, cardTheme: color " + cardTheme);

        }

        console.log('🔍 ChooseThemeField: After update - color.hex:', color.hex, 'cardId:', cardId);
    };

    const handleLinkColorChangeComplete = (color) => {
        console.log('🔍 ChooseThemeField: handleLinkColorChangeComplete called with:', color);
        console.log('🔍 ChooseThemeField: Before update - localLinkColor:', localLinkColor, 'cardLinkColor:', cardLinkColor);

        setLocalLinkColor(color.hex);
        onLinkColorChange(color.hex); // Pass the new link color to the parent

        console.log("handleLinkColorChangeComplete, locallinkcolor: color " + localLinkColor);
        setCardLinkColor(cardId, color.hex);
        console.log("handleLinkColorChangeComplete, cardlinkcolor: color " + cardLinkColor);

        console.log('🔍 ChooseThemeField: After update - color.hex:', color.hex, 'cardId:', cardId);
    };

    const handleClearCardThemeColor = () => {
        // Check if field is restricted
        if (isCardColorRestricted) {
            const message = canRequestChange
                ? 'Card Color is restricted by your subteam. You can submit a change request.'
                : 'Card Color is restricted by your subteam and cannot be updated.';
            if (onRestrictionNotification) {
                onRestrictionNotification(message);
            }
            return;
        }

        setLocalCardThemeColor('#b3b3b3'); // Reset to default grey
        setCardTheme(cardId, '#ffff');
    };

    const handleClearLinkColor = () => {
        setLocalLinkColor('#b3b3b3'); // Reset to default grey
        setCardLinkColor(cardId, '#000');
    };

    const handleMatchLinkToggle = (event) => {
        setMatchLink(event.target.checked);
        if (event.target.checked) {
            setLocalLinkColor(localCardThemeColor); // Match link color to card theme on toggle
            setCardLinkColor(cardId, localCardThemeColor);
        }
    };

    // Initialize local state with context values
    useEffect(() => {
        console.log('🔍 ChooseThemeField: Context values changed:', {
            cardId,
            cardTheme,
            cardLinkColor,
            localCardThemeColor,
            localLinkColor
        });

        if (cardTheme && cardTheme !== localCardThemeColor) {
            console.log('🔍 ChooseThemeField: Updating localCardThemeColor from', localCardThemeColor, 'to', cardTheme);
            setLocalCardThemeColor(cardTheme);
        }
        if (cardLinkColor && cardLinkColor !== localLinkColor) {
            console.log('🔍 ChooseThemeField: Updating localLinkColor from', localLinkColor, 'to', cardLinkColor);
            setLocalLinkColor(cardLinkColor);
        }
    }, [cardTheme, cardLinkColor, localCardThemeColor, localLinkColor]);

    // Debug initial mount
    useEffect(() => {
        console.log('🔍 ChooseThemeField: Component mounted with:', {
            cardId,
            cardTheme,
            cardLinkColor,
            localCardThemeColor,
            localLinkColor,
            cardState: cardState[cardId]
        });
    }, []);

    // Close color pickers when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cardPickerRef.current && !cardPickerRef.current.contains(event.target)) {
                setShowCardThemePicker(false);
            }
            if (linkPickerRef.current && !linkPickerRef.current.contains(event.target)) {
                setShowLinkColorPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <Box sx={{ marginTop: 3 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                Choose Theme
            </Typography>

            <Box sx={{ backgroundColor: "#f7f7f7", padding: 2 }}>
                {/* Card Theme Section */}
                <Box sx={{
                    backgroundColor: "#ffffff",
                    border: '1px solid #dbdbdb',
                    padding: 2,
                    borderRadius: "6px",
                    marginBottom: 2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginRight: "70px" }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                            Card Theme
                        </Typography>
                        {isCardColorRestricted && (
                            <Tooltip title="This field is restricted by your subteam">
                                <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexGrow: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        {/* No Color Circle */}
                        <Box onClick={handleClearCardThemeColor} sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: '#ffffff',
                            border: '0.5px solid #dbdbdb',
                            position: 'relative',
                            cursor: isCardColorRestricted ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isCardColorRestricted ? 0.6 : 1,
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                width: '24px',
                                height: '1px',
                                backgroundColor: '#dbdbdb',
                                transform: 'rotate(-45deg)',
                                transformOrigin: 'center',
                            }} />
                        </Box>

                        {/* Predefined Color Circles */}
                        {colorOptions.map((color, index) => (
                            <Box
                                key={index}
                                onClick={() => {
                                    handleCardThemeChangeComplete({ hex: color });
                                }}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    position: 'relative',
                                    cursor: isCardColorRestricted ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: isCardColorRestricted ? 0.6 : 1,
                                }}
                            >
                                {/* Outer circle */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        border: `2px solid ${localCardThemeColor === color ? '#dbdbdb' : '#ffffff'}`,
                                    }}
                                />
                                {/* Inner color circle */}
                                <Box
                                    sx={{
                                        width: '85%',
                                        height: '85%',
                                        borderRadius: '50%',
                                        backgroundColor: color,
                                    }}
                                />
                            </Box>
                        ))}

                        {/* Custom Color Picker for Card Theme */}
                        <IconButton 
                            onClick={handleCardThemePickerToggle} 
                            disabled={isCardColorRestricted}
                            sx={{
                                position: 'relative',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'transparent',
                                opacity: isCardColorRestricted ? 0.6 : 1,
                                cursor: isCardColorRestricted ? 'not-allowed' : 'pointer',
                            }}>
                            <Box sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                border: `2px solid ${localCardThemeColor === '#b3b3b3' ? '#ffffff' : localCardThemeColor}`,
                            }} />
                            <Box sx={{
                                position: 'absolute',
                                width: '85%',
                                height: '85%',
                                borderRadius: '50%',
                                border: '2px solid #b3b3b3',
                            }} />
                            <ColorizeOutlinedIcon sx={{ color: "#828282", fontSize: 16 }} />
                        </IconButton>

                        {showCardThemePicker && (
                            <Box sx={{ position: 'absolute', top: '40px', right: 0, zIndex: 2 }} ref={cardPickerRef}>
                                <ChromePicker
                                    color={localCardThemeColor || '#ffffff'}
                                    onChange={(color) => {
                                        setLocalCardThemeColor(color.hex)
                                        setCardTheme(cardId, color.hex)

                                    }

                                    }
                                    onChangeComplete={handleCardThemeChangeComplete}
                                    disableAlpha
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Link Color Section */}
                <Box sx={{
                    backgroundColor: "#ffffff",
                    border: '1px solid #dbdbdb',
                    padding: 2,
                    borderRadius: "6px",
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 2,
                    position: 'relative',
                }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginRight: "71px" }}>
                        Link Color
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexGrow: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '5px',
                    }}>
                        {/* No Color Circle for Link Color */}
                        <Box onClick={handleClearLinkColor} sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: '#ffffff',
                            border: '0.5px solid #dbdbdb',
                            position: 'relative',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                width: '24px',
                                height: '1px',
                                backgroundColor: '#dbdbdb',
                                transform: 'rotate(-45deg)',
                                transformOrigin: 'center',
                            }} />
                        </Box>

                        {/* Predefined Color Squares */}
                        {colorOptions.map((color, index) => (
                            <Box
                                key={index}
                                onClick={() => {
                                    handleLinkColorChangeComplete({ hex: color });
                                }}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {/* Outer square */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '8px',
                                        border: `2px solid ${localLinkColor === color ? '#dbdbdb' : '#ffffff'}`,
                                    }}
                                />
                                {/* Inner color square */}
                                <Box
                                    sx={{
                                        width: '90%',
                                        height: '90%',
                                        borderRadius: '8px',
                                        backgroundColor: color,
                                    }}
                                />
                            </Box>
                        ))}

                        {/* Custom Color Picker for Link Color */}
                        <IconButton onClick={handleLinkColorPickerToggle} sx={{
                            position: 'relative',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'transparent',
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                border: `2px solid ${localLinkColor === '#b3b3b3' ? '#ffffff' : localLinkColor}`,
                            }} />
                            <Box sx={{
                                position: 'absolute',
                                width: '85%',
                                height: '85%',
                                borderRadius: '50%',
                                border: '2px solid #b3b3b3',
                            }} />
                            <ColorizeOutlinedIcon sx={{ color: "#828282", fontSize: 16 }} />
                        </IconButton>

                        {showLinkColorPicker && (
                            <Box sx={{ position: 'absolute', top: '40px', right: 0, zIndex: 2 }} ref={linkPickerRef}>
                                <ChromePicker
                                    color={localLinkColor || '#ffffff'}
                                    onChange={(color) => {
                                        setLocalLinkColor(color.hex)
                                        setCardLinkColor(cardId, color.hex)
                                    }




                                    }
                                    onChangeComplete={handleLinkColorChangeComplete}
                                    disableAlpha
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Toggle for Matching Link Color to Card Theme */}
                <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2, justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                        Match Link Icons to Card Theme
                    </Typography>
                    <TeamIosDarkToggle
                        checked={matchLink}
                        onChange={handleMatchLinkToggle}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default TeamChooseThemeField;
